/**
 * Navbar Component
 *
 * Main navigation bar with two variants matching Figma design system.
 * Figma: Navigation component (Node: 482:25981 - Nav Bar Components)
 *
 * Variants:
 * - 'landing' (Node: 479:20375): Landing page - NO search bar, icons only
 * - 'default' (Node: 482:25983): Results/other pages - compact search bar on right
 *
 * Layout:
 * - Desktop: Height 59.481px - Full navigation with taxonomy links
 * - Mobile: Height 48px - Hamburger menu with expandable overlay
 * - Background: White
 * - Border: Bottom border (1px, divider1) on default variant only
 * - Padding: 14px top + 30px horizontal, 0.674px bottom
 * - Left: Taxonomy navigation (Men, Women, Kids, Beauty)
 * - Center: Cosimo logo with BETA badge
 * - Right: [Landing] Icons only (Profile, Bookmark, Cart) | [Default] Compact search + Icons
 *
 * Updated: October 22, 2025 - Added mobile navbar support
 */
"use client";

import { useRef, useState } from "react";

import { useRouter } from "next/navigation";

import { CartDropdown } from "@/components/cart/CartDropdown";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useSearchContext } from "@/contexts/SearchContext";
import { useSignUpPrompt } from "@/contexts/SignUpPromptContext";
import { useActiveCategory } from "@/hooks/useActiveCategory";
import type { BrowseMenuRootSummary } from "@/lib/browse-menu-roots";
import { CART_PATH, COLLECTIONS_PATH, PROFILE_PATH } from "@/lib/route-paths";
import { colors } from "@/styles/design-tokens";

import {
  DESKTOP_NAVBAR_CONTENT_HEIGHT_PX,
  DESKTOP_NAVBAR_TOP_PADDING_PX,
} from "./layout-constants";
import { LogoContainer } from "./LogoContainer";
import { MobileNavbar } from "./MobileNavbar";
import { CompactSearchBar } from "./navigation/CompactSearchBar";
import { SitePageNavigations } from "./navigation/SitePageNavigations";
import { TaxonomyNavigation } from "./navigation/TaxonomyNavigation";

interface NavbarProps {
  variant?: "landing" | "default";
  activeCategory?: string;
  onCategoryChange?: (category: string) => void;
  onSearchSubmit?: (searchMode: "new", gender: string, query: string) => void;
  browseMenuRoots?: BrowseMenuRootSummary[] | null;
}

export function Navbar({
  variant: propVariant = "landing",
  activeCategory: _propActiveCategory,
  onCategoryChange,
  onSearchSubmit,
  browseMenuRoots = null,
}: NavbarProps) {
  const router = useRouter();
  const { getTotalItems } = useCart();
  const { isAnonymous } = useAuth();
  const { showSignUpPrompt } = useSignUpPrompt();
  const { recentSearches } = useSearchContext();
  const detectedActiveCategory = useActiveCategory();
  // Internal category state - not currently used
  // const [internalActiveCategory, setInternalActiveCategory] = useState<string>(
  //   propActiveCategory || "Menswear"
  // );
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [showCartDropdown, setShowCartDropdown] = useState(false);
  const cartIconRef = useRef<HTMLButtonElement>(null);

  // Use prop variant directly
  const variant = propVariant;

  // Use controlled or uncontrolled active category
  // Commented out as not currently used in the component
  // const activeCategory =
  //   propActiveCategory !== undefined
  //     ? propActiveCategory
  //     : internalActiveCategory;

  // Get actual cart count from context
  const cartCount = getTotalItems();
  const safeRecentSearches = recentSearches ?? [];

  const handleCategoryClick = (category: string) => {
    // Update internal state if not controlled (currently not used)
    // if (propActiveCategory === undefined) {
    //   setInternalActiveCategory(category);
    // }

    // Notify parent of category change
    onCategoryChange?.(category);

    // On landing page, parent handles the change
    // On other pages, navigate
    if (variant !== "landing") {
      // TODO: Implement category navigation
      console.debug("Category clicked:", category);
    }
  };

  const handleBookmarkClick = () => {
    if (isAnonymous) {
      showSignUpPrompt("collection");
      return;
    }
    router.push(COLLECTIONS_PATH);
  };

  const handleProfileClick = () => {
    router.push(PROFILE_PATH);
  };

  const handleSignUpClick = () => {
    // The navbar signup CTA opens the auth modal, which is the current primary entry point.
    showSignUpPrompt("general");
  };

  const handleCartClick = () => {
    if (isAnonymous) {
      showSignUpPrompt("cart");
      return;
    }
    router.push(CART_PATH);
  };

  const handleCartMouseEnter = () => {
    if (isAnonymous) return; // Don't show dropdown for anonymous users
    setShowCartDropdown(true);
  };

  const handleCartMouseLeave = () => {
    setShowCartDropdown(false);
  };

  const handleChatClick = () => {
    router.push("/chats");
  };

  const handleCartDropdownClose = () => {
    setShowCartDropdown(false);
  };

  return (
    <>
      {/* Mobile Navbar - Show on screens < 1024px */}
      <div className="block lg:hidden">
        <MobileNavbar variant={variant} browseMenuRoots={browseMenuRoots} />
      </div>

      {/* Desktop Navbar - Show on screens >= 1024px */}
      <nav
        className="hidden w-full lg:block"
        style={{
          backgroundColor: colors.background.white,
          // Landing variant uses divider1, default uses divider.default, hide when mega menu open
          borderBottom: isMegaMenuOpen
            ? "none"
            : variant === "landing"
              ? `1px solid ${colors.divider.divider1}`
              : `1px solid ${colors.divider.default}`,
          paddingTop: `${DESKTOP_NAVBAR_TOP_PADDING_PX}px`,
          paddingLeft: "30px",
          paddingRight: "30px",
          paddingBottom: "0.674px",
        }}
      >
        <div className="flex w-full flex-col items-center">
          <div className="flex w-full flex-col items-center gap-[13px]">
            {/* Top Navigation Container */}
            <div
              className="relative w-full"
              style={{
                height: `${DESKTOP_NAVBAR_CONTENT_HEIGHT_PX}px`,
              }}
            >
              {/* Left: Taxonomy Navigation */}
              <TaxonomyNavigation
                activeCategory={detectedActiveCategory}
                onCategoryClick={handleCategoryClick}
                onMegaMenuChange={setIsMegaMenuOpen}
                browseMenuRoots={browseMenuRoots}
                className="absolute inset-y-0 flex items-center"
                style={{
                  left: "-0.37px",
                }}
              />

              {/* Center: Logo */}
              <div
                className="absolute"
                style={{
                  left: "calc(50% - 0.414px)",
                  top: "19.25px",
                  transform: "translateX(-50%)",
                }}
              >
                <LogoContainer />
              </div>

              {/* Right: Conditional content based on variant */}
              <div
                className="absolute flex items-center gap-[14px]"
                style={{
                  right: "-0.13px",
                  top: "13.99px",
                  height: "31.5px", // Fixed height for consistent positioning
                }}
              >
                {/* Default variant: Show compact search bar */}
                {variant === "default" && (
                  <CompactSearchBar
                    onSubmit={onSearchSubmit}
                    recentSearches={safeRecentSearches
                      .filter((item) => Boolean(item.search_label?.trim()))
                      .slice(0, 4)
                      .map((item) => item.search_label as string)}
                  />
                )}

                {/* Site Page Navigations (both variants) - no padding needed */}
                <SitePageNavigations
                  cartCount={cartCount}
                  isAnonymous={isAnonymous}
                  onChatClick={handleChatClick}
                  onBookmarkClick={handleBookmarkClick}
                  onProfileClick={handleProfileClick}
                  onSignUpClick={handleSignUpClick}
                  onCartClick={handleCartClick}
                  onCartMouseEnter={handleCartMouseEnter}
                  onCartMouseLeave={handleCartMouseLeave}
                  cartIconRef={cartIconRef}
                />
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Cart Dropdown - Desktop only */}
      <div
        className="hidden lg:block"
        onMouseEnter={handleCartMouseEnter}
        onMouseLeave={handleCartMouseLeave}
      >
        <CartDropdown
          isOpen={showCartDropdown}
          onViewCart={handleCartClick}
          anchorEl={cartIconRef.current}
          onClose={handleCartDropdownClose}
        />
      </div>
    </>
  );
}

// Backwards compatibility export
export { Navbar as MainNavbar };
